import { PureComponent, ComponentProps, SyntheticEvent } from 'react';

import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import Textfield from '@atlaskit/textfield';
import { PositionType } from '@atlaskit/tooltip';

import messages from '../i18n';
import * as styles from '../styles';

import CopyButton from './copy-button';

type CopyableTextFieldStatelessProps = ComponentProps<typeof Textfield> & {
  intl: IntlShape;
  initialValue?: string;
  onChange?: (e: SyntheticEvent<HTMLInputElement>) => void;
  onCopy?: (text: string) => void;
  forwardedRef?: (ref?: HTMLInputElement) => void;
  tooltipPosition?: PositionType;
};

class CopyableTextFieldStatelessInner extends PureComponent<CopyableTextFieldStatelessProps> {
  textField: HTMLInputElement | undefined;

  static defaultProps = {
    onChange: () => {},
    onCopy: () => {},
    /* eslint react/default-props-match-prop-types:warn */
    value: '',
  };

  focus() {
    if (this.textField) {
      this.textField.focus();
    }
  }

  select() {
    if (this.textField && this.textField) {
      this.textField.select();
    }
  }

  handleClick = () => {
    this.select();
    this.focus();

    if (this.props.onCopy) {
      this.props.onCopy(this.stringifiedValue());
    }
  };

  handleTextFieldRef = (textField: HTMLInputElement | undefined) => {
    this.textField = textField;
    if (this.props.forwardedRef) {
      this.props.forwardedRef(textField);
    }
  };

  stringifiedValue() {
    const { value } = this.props;
    if (typeof value === 'number') {
      return value.toString();
    }
    return value as string;
  }

  render() {
    // Destructuring onCopy and initialValue so that they don't get passed in
    // to FieldTextStateless that doesn't have those attributes typed.
    const {
      intl,
      value,
      onCopy,
      initialValue,
      forwardedRef,
      tooltipPosition,
      ...otherProps
    } = this.props;

    return (
      <styles.Container>
        <styles.TextField>
          <Textfield
            {...otherProps}
            aria-label={intl.formatMessage(messages.copyableInput)}
            ref={this.handleTextFieldRef}
            value={value}
          />
        </styles.TextField>
        {/* CopyButton only takes in a String type for `value` vs
        FieldTextStateless handles both String and Number */}
        <CopyButton
          onClick={this.handleClick}
          value={this.stringifiedValue()}
          tooltipPosition={tooltipPosition}
        />
      </styles.Container>
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default injectIntl(CopyableTextFieldStatelessInner);
